import { useEffect, useRef, useState } from "react";
import { Dropdown, Menu, message, TableColumnsType } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { ReactComponent as MenuIcon } from "@assets/images/vertical-dots.svg";

import Table from "@components/Table";
import Button from "@components/Button";
import { checkPermission } from "@utils";
import { permissions } from "@router";
import {
  ErrorServices,
  useDeleteCompanyJobFunctionMutation,
  useFetchPaginatedCompanyJobFunctionsQuery,
  useDownloadCompanyJobFunctionsMutation,
  useUploadCompanyJobFunctionsMutation,
  useFetchCompaniesQuery,
} from "@services";
import AddCompanyJobFunction from "./AddJobFunction";
import EditCompanyJobFunction from "./EditJobFunction";
import { useTypedSelector } from "@/hooks";
import { handleCsvUpload } from "@/utils/uploadCsv";
import CompanyList from "@/components/List/company-list";

type JobSubFunctionType = {
  id: number;
  name: string;
  description?: string | null;
};

type JobFunctionType = {
  id: number;
  name: string;
  description?: string | null;
  sub_job_functions: Array<JobSubFunctionType>;
};

const CompanyJobFunction = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const jf_id = useRef<any>(null);
  const inputRef = useRef<any>(null);

  const { user, permissions: per } = useTypedSelector(state => state.auth);

  const [isAddJFVisible, setIsAddJFVisible] = useState(false);
  const [isEditJFVisible, setIsEditJFVisible] = useState(false);
  const [jfItem, setJfItem] = useState<JobFunctionType | {}>({});

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies = companiesData?.data || [];

  const { data: jobFunctionsData, isLoading: isLoadingJobFunctions } =
    useFetchPaginatedCompanyJobFunctionsQuery(
      {
        company_id: companyId!,
        page,
        page_size: pageSize,
      },
      {
        skip: !companyId, // ✅ added
      }
    );

  const jobFunctions = jobFunctionsData?.data || [];
  const pagination = jobFunctionsData?.pagination;

  const [deleteJobFunction] = useDeleteCompanyJobFunctionMutation();
  const [uploadJobFunction, { isLoading: isUploading }] =
    useUploadCompanyJobFunctionsMutation();

  const [downloadJobFunctions, { isLoading }] =
    useDownloadCompanyJobFunctionsMutation();

  const handleActionDropdown = ({
    item,
    key,
    domEvent,
  }: {
    key: string;
    domEvent:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>;
    item: JobFunctionType;
  }) => {
    domEvent.stopPropagation();

    if (key === "2") {
      removeJobFunction(item?.id.toString());
    } else {
      onEditJf(item);
    }
  };

  const columns: TableColumnsType<JobFunctionType> = [
    {
      title: "Job Function",
      key: "name",
      dataIndex: "name",
      width: "20%",
    },
    {
      title: "Job Sub-Function",
      key: "sub_job_functions",
      width: "55%",
      render: ({ sub_job_functions }) => {
        if (Array.isArray(sub_job_functions) && sub_job_functions.length) {
          return sub_job_functions
            .map((val: JobSubFunctionType) => val.name)
            .join(", ");
        }
        return null;
      },
    },
    ...((per?.includes("UPDATE_JOB_FUNCTION") ||
    per?.includes("DELETE_JOB_FUNCTION") ||
    per?.includes("all")
      ? [
          {
            title: "Actions",
            key: "action",
            fixed: "right",
            width: "10%",
            render: (item: JobFunctionType) => {
              const menu = (
                <Menu
                  onClick={(info: any) =>
                    handleActionDropdown({
                      item,
                      key: info.key,
                      domEvent: info.domEvent,
                    })
                  }
                  tabIndex={1}
                >
                  {(per?.includes("UPDATE_JOB_FUNCTION") ||
                    per?.includes("all")) && (
                    <Menu.Item key="1">Edit</Menu.Item>
                  )}

                  {(per?.includes("DELETE_JOB_FUNCTION") ||
                    per?.includes("all")) && (
                    <Menu.Item key="2" danger>
                      Delete
                    </Menu.Item>
                  )}
                </Menu>
              );

              return (
                <div className="table__action__menu">
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <MenuIcon
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </Dropdown>
                </div>
              );
            },
          },
        ]
      : []) as any),
  ];

  const onRowClick = (data: JobFunctionType) => {
    history.push(
      `/company-job-functions/company/${companyId}/job_function/${data.id}`,
      {
        companyId: companyId,
      }
    );
  };

  const onEditJf = async (item: JobFunctionType) => {
    try {
      setJfItem(item);
      setIsEditJFVisible(true);
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const removeJobFunction = async (id: string) => {
    try {
      jf_id.current = id;

      await deleteJobFunction({
        company_id: companyId,
        company_job_function_id: id,
      }).unwrap();

      message.success("Company Job Function Deleted Successfully");
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const downloadFile = async () => {
    try {
      const blob = await downloadJobFunctions({
        company_id: companyId,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = "company-job-functions.csv"; // adjust if needed
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      ErrorServices(err);
    }
  };

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        <div className="flex justify-between items-center mt-6 px-4">
          <h2 className="font-bold text-xl text-themeBlue text-left">
            Select the Company
          </h2>
        </div>

        {isFetchingCompanies ? (
          <div className="flex justify-center items-center h-[400px]">
            <LoadingOutlined style={{ fontSize: 60 }} />
          </div>
        ) : (
          <main className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full">
              <CompanyList
                companiesList={companies}
                onSelectCompany={setSelectedCompany}
                selectedCompany={selectedCompany}
              />

              {/* ✅ SAME ORIGINAL BUTTON BEHAVIOR */}
              {selectedCompany && (
                <div className="mt-8 flex gap-2 sticky bottom-2">
                  <button
                    onClick={() => setCompanyId(selectedCompany)}
                    className="flex-1 bg-themeBlue text-white px-5 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    {
                      companies.find((item: any) => item.id == selectedCompany)
                        ?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      <AddCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsAddJFVisible}
        isVisible={isAddJFVisible}
      />

      <EditCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsEditJFVisible}
        isVisible={isEditJFVisible}
        jfItem={jfItem}
      />

      <div className="appTitle text-base font-[500] mx-5">
        Company Job Functions
      </div>

      <div className="jobCreationBar sticky top-0 z-10 bg-white px-14 py-6 rounded-lg flex justify-between items-center my-7">
        <div className="title text-themeBlue font-[500] text-3xl">
          Company Job Functions & Sub Functions
        </div>

        {checkPermission(permissions.CREATE_JOB_SUB_FUNCTION) && (
          <div className="action-buttons flex items-center gap-2">
            <input
              id="myInput"
              type="file"
              ref={inputRef}
              hidden={true}
              accept=".csv,text/csv"
              onChange={e =>
                handleCsvUpload(e, formData =>
                  uploadJobFunction({
                    company_id: companyId,
                    formData,
                  })
                )
              }
            />

            <Button variant="upload" onClick={() => inputRef?.current?.click()}>
              {isUploading ? (
                <LoadingOutlined color="white" className="spinner" />
              ) : (
                "Upload Job Functions"
              )}
            </Button>

            <Button variant="download" onClick={downloadFile}>
              Download Job Functions
            </Button>

            <Button variant="add" onClick={() => setIsAddJFVisible(true)}>
              Add New Job
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white px-14 py-6 rounded-lg">
        <Table
          data={jobFunctions}
          columns={columns}
          onRowClick={onRowClick}
          isLoading={isLoadingJobFunctions}
          pagination={true}
          count={pagination?.count}
          onChangePage={setPage}
          onPageSizeChange={setPageSize}
          page={page}
        />
      </div>
    </>
  );
};

export default CompanyJobFunction;
